import mqtt, { type MqttClient } from 'mqtt';
import { readSession } from '@/shared/api/session';

/**
 * The panel's one and only MQTT connection (§5.5).
 *
 * ⚠️ **One instance, module-scoped, and features subscribe through it rather than building their
 * own.** A broker counts connections, not components: a client per screen means a control room that
 * opens one per panel, and MQTT answers a duplicate client id by disconnecting the previous holder —
 * so they would also knock each other off. One connection, many subscriptions, is the shape the
 * protocol expects.
 *
 * ⚠️ **It authenticates with the same JWT as the REST API (§8.2)** — username is the user id, the
 * password is the access token. There is no second credential to issue, revoke or forget: an
 * operator signing out loses the broker with it.
 */

/**
 * Where the broker is. Empty disables MQTT entirely rather than retrying a URL that is not set.
 *
 * ⚠️ **A value starting with `/` is a path on the panel's own host, and production uses one.**
 * Until 11 October 2026 `.env.production` did not set this at all, so a developer's `.env.local`
 * — `ws://localhost:8083/mqtt` — was what every deployed panel shipped: the live map in an
 * operator's browser asked their own computer for a broker and never moved. The build before that
 * carried an absolute address on a domain the charity then left. A path cannot go stale either way.
 */
const url = brokerUrl(import.meta.env['VITE_MQTT_URL'] ?? '');

function brokerUrl(configured: string): string {
  if (!configured.startsWith('/')) {
    return configured;
  }

  const scheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${scheme}//${window.location.host}${configured}`;
}

let client: MqttClient | null = null;

/** The token the live connection was opened with, so a renewed one can be noticed. */
let connectedWith: string | null = null;

/**
 * The `sub` claim of a JWT, read without verifying it.
 *
 * ⚠️ **This is a claim, never a fact, and nothing in the panel may trust it.** It exists only to
 * tell the broker which user we say we are; the broker verifies the signature and — through
 * `verify_claims.sub` — refuses the connection if this does not match the token it just checked.
 * That check is what stops an operator presenting their own valid token while claiming a
 * colleague's id, and it lives in the broker precisely so a client cannot skip it.
 */
function subjectOf(token: string): string | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;

    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const claims: unknown = JSON.parse(json);

    if (typeof claims === 'object' && claims !== null && 'sub' in claims) {
      const sub = (claims as { sub: unknown }).sub;
      return typeof sub === 'string' ? sub : null;
    }
  } catch {
    // A token we cannot read is a token we cannot connect with. Handled by the null below.
  }

  return null;
}

/**
 * The shared client, or null when there is no broker or no session.
 *
 * ⚠️ Null is a supported state, not a failure. The panel's data all comes from the API; MQTT only
 * makes it live. A development machine with no broker must still run the whole panel, a signed-out
 * operator has nothing to subscribe to, and a broker that is down must not take the control room
 * with it.
 */
export function mqttClient(): MqttClient | null {
  if (!url) {
    return null;
  }

  const session = readSession();
  const token = session?.accessToken ?? null;
  const userId = token ? subjectOf(token) : null;

  if (!token || !userId) {
    closeClient();
    return null;
  }

  // ⚠️ A renewed token means a new connection, and this is not tidiness.
  //
  // §12.1 keeps an access token to fifteen minutes, and EMQX disconnects a client when the token it
  // authenticated with expires. mqtt.js would then reconnect on its own — with the SAME expired
  // password, forever. The control room's live feed would stop a quarter of an hour after sign-in
  // and never come back, while every REST call kept working.
  if (client && connectedWith !== token) {
    closeClient();
  }

  if (client) {
    return client;
  }

  connectedWith = token;
  client = mqtt.connect(url, {
    username: userId,
    password: token,

    // ⚠️ Unique per tab. Two panels sharing a client id disconnect each other in a loop, and the
    // symptom is a live map that stops updating for whoever opened it first.
    clientId: `aoun-admin-${Math.random().toString(16).slice(2, 10)}`,

    // A control room reconnects on its own: an operator who has to reload the page to get live
    // events back is an operator who will not notice they stopped.
    reconnectPeriod: 5000,

    // ⚠️ A clean session, unlike the app's (§8.5). A phone needs the queue the broker held while
    // its owner was in a wadi; a panel that was closed wants what is true NOW, not a replay of
    // every event it missed overnight.
    clean: true,
  });

  return client;
}

/** Ends the connection, if there is one. Called on sign-out and on a token change. */
export function closeClient(): void {
  client?.end(true);
  client = null;
  connectedWith = null;
}

/**
 * Subscribes to [topic] until the returned function is called.
 *
 * ⚠️ It unsubscribes on cleanup, and that matters more than it looks: React mounts and unmounts a
 * component many times over a shift, and a subscription that outlives its component keeps the
 * broker delivering to a handler that updates state nobody is showing.
 */
export function subscribe(topic: string, onMessage: (payload: unknown) => void): () => void {
  const active = mqttClient();
  if (!active) {
    return () => {};
  }

  const handler = (received: string, payload: Uint8Array) => {
    if (received !== topic) {
      return;
    }

    try {
      onMessage(JSON.parse(new TextDecoder().decode(payload)));
    } catch {
      // ⚠️ A malformed payload is ignored, never thrown. This runs in the client's event handler,
      // and an exception escaping it takes the whole connection down — so one bad message would
      // stop every live update in the room.
    }
  };

  active.on('message', handler);
  active.subscribe(topic, { qos: 1 });

  return () => {
    active.off('message', handler);
    active.unsubscribe(topic);
  };
}
